// Drawing engine for the signage image editor (SignageImageEditor.js).
//
// A "project" is plain JSON — canvas size, a background, and an ordered list
// of layers — so it can be saved with the asset and reopened later. This
// module only draws it; it has no React and no DOM dependencies beyond a
// canvas factory (env.createCanvas), so the same code renders the live
// preview, the exported JPEG, and runs under node-canvas in tests.
//
// Project shape (coordinates are output pixels):
//   { v: 1, width, height,
//     background: { type: "color"|"gradient"|"blur"|"image",
//                   color, color2, angle, blur, dim, src },
//     layers: [ image | text | shape ] }
//   image: { id, type: "image", src, x, y, w, h, rotate (0/90/180/270),
//            flipX, brightness, contrast, saturation (100 = unchanged),
//            opacity, radius, crop }
//            crop = { x, y, w, h } as fractions (0–1) of the rotated/flipped
//            image — the part shown in the layer's box; absent = all of it.
//   text:  { id, type: "text", text, x, y, w, fontFamily, fontSize,
//            bold, italic, color, align, lineHeight, shadow, outlineColor,
//            outlineWidth, bgColor, bgOpacity, bgPadding, bgRadius, opacity }
//   shape: { id, type: "shape", shape: "rect"|"ellipse", x, y, w, h, color,
//            opacity, radius }
// `src` is "source" (the image being edited) or "asset:<id>" (another
// library image); callers resolve both to loaded images via `images`.

export const PROJECT_VERSION = 1;

// ---- geometry ----

// Width/height of an image as drawn, after a quarter-turn rotation.
export const rotatedSize = (img, rotate) =>
  rotate === 90 || rotate === 270
    ? { w: img.height, h: img.width }
    : { w: img.width, h: img.height };

const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };
export const cropOf = (layer) => layer.crop || FULL_CROP;

// Size of an image layer's visible part (rotation and crop applied), in the
// image's own pixels — what Fit/Fill and aspect ratios work from.
export const croppedSize = (img, layer) => {
  const r = rotatedSize(img, layer.rotate || 0);
  const c = cropOf(layer);
  return { w: r.w * c.w, h: r.h * c.h };
};

// Keep a crop pointing at the same part of the picture when the image turns
// a quarter (dir 1 = clockwise, -1 = counter-clockwise) or is mirrored.
export const rotateCrop = (crop, dir) => {
  if (!crop) return crop;
  return dir > 0
    ? { x: 1 - crop.y - crop.h, y: crop.x, w: crop.h, h: crop.w }
    : { x: crop.y, y: 1 - crop.x - crop.w, w: crop.h, h: crop.w };
};
export const flipCrop = (crop) => (crop ? { ...crop, x: 1 - crop.x - crop.w } : crop);

// Where the WHOLE (uncropped) image sits on the canvas, given the layer's
// box shows only its crop — the frame the crop tool works in.
export const uncroppedRect = (layer) => {
  const c = cropOf(layer);
  const w = layer.w / c.w;
  const h = layer.h / c.h;
  return { x: layer.x - c.x * w, y: layer.y - c.y * h, w, h };
};

// Place `inner` inside `outer` (both {w,h}) — "contain" or "cover".
export const fitRect = (inner, outer, mode = "contain") => {
  const scale =
    mode === "cover"
      ? Math.max(outer.w / inner.w, outer.h / inner.h)
      : Math.min(outer.w / inner.w, outer.h / inner.h);
  const w = inner.w * scale;
  const h = inner.h * scale;
  return { x: (outer.w - w) / 2, y: (outer.h - h) / 2, w, h };
};

const roundRectPath = (ctx, x, y, w, h, r) => {
  const rr = Math.max(0, Math.min(r || 0, Math.abs(w) / 2, Math.abs(h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
};

const hexToRgba = (hex, alpha = 1) => {
  const m = String(hex || "").replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const n = parseInt(full || "000000", 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// ---- text layout ----

export const fontString = (layer, size = layer.fontSize) =>
  `${layer.italic ? "italic " : ""}${layer.bold ? 700 : 400} ${size}px "${
    layer.fontFamily || "Arial"
  }", Arial, sans-serif`;

/**
 * Wrap a text layer into lines that fit its box width (explicit newlines
 * kept; a single word wider than the box gets its own line). Returns
 * { lines, lineHeightPx, height } — height is the text block, no padding.
 */
export const layoutText = (ctx, layer) => {
  ctx.save();
  ctx.font = fontString(layer);
  const maxW = Math.max(10, layer.w);
  const lines = [];
  for (const para of String(layer.text ?? "").split("\n")) {
    const words = para.split(/(\s+)/).filter((t) => t.length);
    let line = "";
    for (const word of words) {
      const next = line + word;
      if (line && ctx.measureText(next.trimEnd()).width > maxW && word.trim()) {
        lines.push(line.trimEnd());
        line = word.trimStart();
      } else {
        line = next;
      }
    }
    lines.push(line.trimEnd());
  }
  ctx.restore();
  const lineHeightPx = layer.fontSize * (layer.lineHeight || 1.15);
  return { lines, lineHeightPx, height: lines.length * lineHeightPx };
};

// Axis-aligned bounds of a layer on the canvas (text: its wrapped block).
export const layerBounds = (ctx, layer) => {
  if (layer.type === "text") {
    const { height } = layoutText(ctx, layer);
    return { x: layer.x, y: layer.y, w: layer.w, h: height };
  }
  return { x: layer.x, y: layer.y, w: layer.w, h: layer.h };
};

// ---- image preparation (cached) ----

// Rotated / flipped / color-adjusted copy of an image, capped at `maxSide`
// on its long edge. Pixel math instead of ctx.filter so it looks the same in
// every browser (Safari's canvas filter support is recent) and in node.
const prepCache = new WeakMap();

const prepareImage = (env, img, layer, maxSide = 2400) => {
  const key = [
    layer.rotate || 0,
    layer.flipX ? 1 : 0,
    layer.brightness ?? 100,
    layer.contrast ?? 100,
    layer.saturation ?? 100,
    maxSide,
  ].join("|");
  let perImage = prepCache.get(img);
  if (!perImage) {
    perImage = new Map();
    prepCache.set(img, perImage);
  }
  if (perImage.has(key)) return perImage.get(key);

  const rot = layer.rotate || 0;
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const sw = Math.max(1, Math.round(img.width * scale));
  const sh = Math.max(1, Math.round(img.height * scale));
  const { w, h } = rotatedSize({ width: sw, height: sh }, rot);
  const canvas = env.createCanvas(w, h);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.translate(w / 2, h / 2);
  // Mirror after rotating, so "flipX" is always left↔right as seen.
  if (layer.flipX) ctx.scale(-1, 1);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.drawImage(img, -sw / 2, -sh / 2, sw, sh);

  const b = (layer.brightness ?? 100) / 100;
  const c = (layer.contrast ?? 100) / 100;
  const s = (layer.saturation ?? 100) / 100;
  if (b !== 1 || c !== 1 || s !== 1) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const data = ctx.getImageData(0, 0, w, h);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      let r = px[i] * b;
      let g = px[i + 1] * b;
      let bl = px[i + 2] * b;
      r = (r - 128) * c + 128;
      g = (g - 128) * c + 128;
      bl = (bl - 128) * c + 128;
      const gray = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
      px[i] = gray + (r - gray) * s;
      px[i + 1] = gray + (g - gray) * s;
      px[i + 2] = gray + (bl - gray) * s;
    }
    ctx.putImageData(data, 0, 0);
  }
  perImage.set(key, canvas);
  // Slider drags produce many variants — keep only the latest few.
  while (perImage.size > 4) perImage.delete(perImage.keys().next().value);
  return canvas;
};

// Soft blur by shrinking then scaling back up (twice) — cheap, smooth, and
// works everywhere, unlike ctx.filter.
const blurCache = new WeakMap();
const blurredCover = (env, img, width, height, amount) => {
  const key = `${width}x${height}|${amount}`;
  let perImage = blurCache.get(img);
  if (!perImage) {
    perImage = new Map();
    blurCache.set(img, perImage);
  }
  if (perImage.has(key)) return perImage.get(key);

  const out = env.createCanvas(width, height);
  const octx = out.getContext("2d");
  const rect = fitRect({ w: img.width, h: img.height }, { w: width, h: height }, "cover");
  const factor = Math.max(2, amount / 2);
  const tw = Math.max(2, Math.round(width / factor));
  const th = Math.max(2, Math.round(height / factor));
  const small = env.createCanvas(tw, th);
  const sctx = small.getContext("2d");
  sctx.imageSmoothingQuality = "high";
  sctx.drawImage(img, rect.x / factor, rect.y / factor, rect.w / factor, rect.h / factor);
  const mid = env.createCanvas(tw * 2, th * 2);
  const mctx = mid.getContext("2d");
  mctx.imageSmoothingQuality = "high";
  mctx.drawImage(small, 0, 0, tw * 2, th * 2);
  octx.imageSmoothingQuality = "high";
  octx.drawImage(mid, 0, 0, width, height);
  perImage.set(key, out);
  while (perImage.size > 4) perImage.delete(perImage.keys().next().value);
  return out;
};

// ---- drawing ----

const drawBackground = (ctx, env, project, images) => {
  const { width, height } = project;
  const bg = project.background || { type: "color", color: "#000000" };
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, width, height);

  if (bg.type === "gradient") {
    const a = ((bg.angle ?? 135) * Math.PI) / 180;
    const cx = width / 2;
    const cy = height / 2;
    const half = (Math.abs(Math.cos(a)) * width + Math.abs(Math.sin(a)) * height) / 2;
    const grad = ctx.createLinearGradient(
      cx - Math.cos(a) * half,
      cy - Math.sin(a) * half,
      cx + Math.cos(a) * half,
      cy + Math.sin(a) * half
    );
    grad.addColorStop(0, bg.color || "#0f172a");
    grad.addColorStop(1, bg.color2 || "#1e3a8a");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
    return;
  }

  const img = (bg.type === "blur" || bg.type === "image") && images[bg.src || "source"];
  if (img) {
    if (bg.type === "blur") {
      ctx.drawImage(blurredCover(env, img, width, height, bg.blur ?? 40), 0, 0);
    } else {
      const r = fitRect({ w: img.width, h: img.height }, { w: width, h: height }, "cover");
      ctx.drawImage(img, r.x, r.y, r.w, r.h);
    }
    if (bg.dim) {
      ctx.fillStyle = `rgba(0, 0, 0, ${Math.min(0.9, bg.dim)})`;
      ctx.fillRect(0, 0, width, height);
    }
    return;
  }

  ctx.fillStyle = bg.color || "#000000";
  ctx.fillRect(0, 0, width, height);
};

const drawImageLayer = (ctx, env, layer, images) => {
  const img = images[layer.src];
  if (!img) return;
  const prepared = prepareImage(env, img, layer);
  ctx.save();
  ctx.globalAlpha = layer.opacity ?? 1;
  if (layer.radius) {
    roundRectPath(ctx, layer.x, layer.y, layer.w, layer.h, layer.radius);
    ctx.clip();
  }
  ctx.imageSmoothingQuality = "high";
  const c = cropOf(layer);
  ctx.drawImage(
    prepared,
    c.x * prepared.width,
    c.y * prepared.height,
    c.w * prepared.width,
    c.h * prepared.height,
    layer.x,
    layer.y,
    layer.w,
    layer.h
  );
  ctx.restore();
};

const drawShapeLayer = (ctx, layer) => {
  ctx.save();
  ctx.globalAlpha = layer.opacity ?? 1;
  ctx.fillStyle = layer.color || "#0369a1";
  if (layer.shape === "ellipse") {
    ctx.beginPath();
    ctx.ellipse(
      layer.x + layer.w / 2,
      layer.y + layer.h / 2,
      Math.abs(layer.w / 2),
      Math.abs(layer.h / 2),
      0,
      0,
      Math.PI * 2
    );
    ctx.fill();
  } else {
    roundRectPath(ctx, layer.x, layer.y, layer.w, layer.h, layer.radius || 0);
    ctx.fill();
  }
  ctx.restore();
};

const drawTextLayer = (ctx, layer) => {
  const { lines, lineHeightPx, height } = layoutText(ctx, layer);
  ctx.save();
  ctx.globalAlpha = layer.opacity ?? 1;

  if (layer.bgColor && (layer.bgOpacity ?? 1) > 0) {
    const pad = layer.bgPadding ?? Math.round(layer.fontSize * 0.35);
    ctx.fillStyle = hexToRgba(layer.bgColor, layer.bgOpacity ?? 1);
    roundRectPath(
      ctx,
      layer.x - pad,
      layer.y - pad,
      layer.w + pad * 2,
      height + pad * 2,
      layer.bgRadius ?? 0
    );
    ctx.fill();
  }

  ctx.font = fontString(layer);
  ctx.textBaseline = "middle";
  const align = layer.align || "left";
  ctx.textAlign = align;
  const tx =
    align === "center"
      ? layer.x + layer.w / 2
      : align === "right"
        ? layer.x + layer.w
        : layer.x;

  lines.forEach((line, i) => {
    const ty = layer.y + lineHeightPx * i + lineHeightPx / 2;
    if (layer.outlineWidth > 0) {
      ctx.save();
      ctx.lineJoin = "round";
      ctx.lineWidth = layer.outlineWidth * 2;
      ctx.strokeStyle = layer.outlineColor || "#000000";
      ctx.strokeText(line, tx, ty);
      ctx.restore();
    }
    ctx.save();
    if (layer.shadow) {
      ctx.shadowColor = "rgba(0, 0, 0, 0.65)";
      ctx.shadowBlur = layer.fontSize * 0.12;
      ctx.shadowOffsetX = layer.fontSize * 0.04;
      ctx.shadowOffsetY = layer.fontSize * 0.05;
    }
    ctx.fillStyle = layer.color || "#ffffff";
    ctx.fillText(line, tx, ty);
    ctx.restore();
  });
  ctx.restore();
};

/**
 * Draw a whole project onto `ctx` (sized project.width × project.height).
 * images: { [src]: loaded image } — missing ones are simply skipped.
 */
export const renderProject = (ctx, project, images, env) => {
  ctx.save();
  drawBackground(ctx, env, project, images);
  for (const layer of project.layers || []) {
    if (layer.hidden) continue;
    if (layer.type === "image") drawImageLayer(ctx, env, layer, images);
    else if (layer.type === "shape") drawShapeLayer(ctx, layer);
    else if (layer.type === "text") drawTextLayer(ctx, layer);
  }
  ctx.restore();
};

// ---- project helpers ----

let idCounter = 0;
export const newLayerId = () => `l${Date.now().toString(36)}${(idCounter++).toString(36)}`;

// Output sizes offered in the editor. "image" = keep the photo's own shape,
// long edge capped at 1920.
export const CANVAS_PRESETS = [
  { key: "landscape", label: "TV landscape", width: 1920, height: 1080 },
  { key: "portrait", label: "TV portrait", width: 1080, height: 1920 },
  { key: "image", label: "Photo's own shape" },
];

export const sizeForPreset = (preset, img) => {
  const p = CANVAS_PRESETS.find((c) => c.key === preset) || CANVAS_PRESETS[0];
  if (p.width) return { width: p.width, height: p.height };
  const scale = Math.min(1, 1920 / Math.max(img.width, img.height));
  return {
    width: Math.max(1, Math.round(img.width * scale)),
    height: Math.max(1, Math.round(img.height * scale)),
  };
};

// An image layer placed to fit ("contain") or fill ("cover") the canvas.
export const placeImage = (layer, img, canvas, mode) => {
  const drawn = croppedSize(img, layer);
  const r = fitRect(drawn, { w: canvas.width, h: canvas.height }, mode);
  return { ...layer, x: r.x, y: r.y, w: r.w, h: r.h };
};

// Starting project for an image that has never been through the editor:
// landscape TV canvas, the photo fitted in, blurred copy of itself behind.
export const defaultProject = (img) => {
  const size = sizeForPreset("landscape", img);
  const base = {
    id: newLayerId(),
    type: "image",
    src: "source",
    rotate: 0,
    flipX: false,
    brightness: 100,
    contrast: 100,
    saturation: 100,
    opacity: 1,
    radius: 0,
  };
  return {
    v: PROJECT_VERSION,
    preset: "landscape",
    ...size,
    background: {
      type: "blur",
      src: "source",
      blur: 40,
      dim: 0.25,
      color: "#0f172a",
      color2: "#1e3a8a",
      angle: 135,
    },
    layers: [placeImage(base, img, size, "contain")],
  };
};

export const newTextLayer = (project) => {
  const fontSize = Math.round(project.height * 0.08);
  const w = Math.round(project.width * 0.7);
  return {
    id: newLayerId(),
    type: "text",
    text: "Your text here",
    x: Math.round((project.width - w) / 2),
    y: Math.round(project.height * 0.12),
    w,
    fontFamily: "Montserrat",
    fontSize,
    bold: true,
    italic: false,
    color: "#ffffff",
    align: "center",
    lineHeight: 1.15,
    shadow: true,
    outlineColor: "#000000",
    outlineWidth: 0,
    bgColor: "",
    bgOpacity: 0.7,
    bgPadding: Math.round(fontSize * 0.35),
    bgRadius: Math.round(fontSize * 0.2),
    opacity: 1,
  };
};

export const newShapeLayer = (project, shape = "rect") => {
  const w = Math.round(project.width * (shape === "ellipse" ? 0.25 : 1));
  const h = Math.round(project.height * (shape === "ellipse" ? 0.25 : 0.18));
  return {
    id: newLayerId(),
    type: "shape",
    shape,
    x: Math.round((project.width - w) / 2),
    y: shape === "ellipse" ? Math.round((project.height - h) / 2) : project.height - h,
    w,
    h,
    color: "#0369a1",
    opacity: 0.85,
    radius: 0,
  };
};
